import { useCallback, useEffect, useState } from "react";

import { confirmable } from "react-confirm";

import { Button, Header, Icon, Modal } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

type PromptModalProps = {
  show?: boolean;
  dismiss?: () => void;
  title: string;
  prompt: string;
  proceed: () => void;
  onComplete: (value: string) => void;
};

const PromptModal = ({
  show,
  dismiss,
  title,
  prompt,
  proceed,
  onComplete,
}: PromptModalProps) => {
  const [value, setValue] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const complete = useCallback(() => {
    onComplete(value);
    proceed();
  }, [value, onComplete, proceed]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === 13 && !confirmed) {
        setConfirmed(true);
        complete();
      }
    },
    [confirmed, complete],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!!show}
      onClose={dismiss ?? (() => {})}
      basic
      size="small"
      className="no-scroll"
    >
      <Header content={title} />
      <Modal.Content>{prompt}</Modal.Content>
      <Modal.Content>
        <Input
          fluid
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button basic color="red" inverted onClick={dismiss ?? (() => {})}>
          <Icon name="remove" /> Cancel
        </Button>
        <Button color="green" inverted onClick={complete}>
          <Icon name="checkmark" /> OK
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default confirmable(PromptModal);
